import { useState, useEffect, useCallback } from "react";
import { AlertCircle, Loader2, Search, ChevronLeft, ChevronRight, ExternalLink, Info } from "lucide-react";
import webScraperService, {
  WebScraperSkipReason,
  WebScraperSkippedUrl,
} from "../../../services/web-scraper.service";

interface SkippedUrlsPanelProps {
  jobId: string;
  refreshKey: number;
}

const PAGE_SIZE = 25;

const REASON_LABELS: Record<WebScraperSkipReason, string> = {
  [WebScraperSkipReason.ROBOTS_TXT_DISALLOWED]: "Blocked by robots.txt",
  [WebScraperSkipReason.MAX_DEPTH_EXCEEDED]:    "Beyond max depth",
  [WebScraperSkipReason.FILE_TYPE_NOT_ALLOWED]: "File type filtered",
  [WebScraperSkipReason.FILE_TOO_LARGE]:        "File too large",
  [WebScraperSkipReason.DOMAIN_NOT_ALLOWED]:    "External domain",
  [WebScraperSkipReason.HEAD_REQUEST_FAILED]:   "HEAD request failed",
  [WebScraperSkipReason.UNSUPPORTED_PROTOCOL]:  "Unsupported protocol",
  [WebScraperSkipReason.UNKNOWN_CONTENT_TYPE]:  "Unknown content type",
};

const REASON_COLORS: Record<WebScraperSkipReason, string> = {
  [WebScraperSkipReason.ROBOTS_TXT_DISALLOWED]: "bg-red-100 text-red-700 border-red-200",
  [WebScraperSkipReason.MAX_DEPTH_EXCEEDED]:    "bg-amber-100 text-amber-700 border-amber-200",
  [WebScraperSkipReason.FILE_TYPE_NOT_ALLOWED]: "bg-blue-100 text-blue-700 border-blue-200",
  [WebScraperSkipReason.FILE_TOO_LARGE]:        "bg-orange-100 text-orange-700 border-orange-200",
  [WebScraperSkipReason.DOMAIN_NOT_ALLOWED]:    "bg-gray-100 text-gray-700 border-gray-200",
  [WebScraperSkipReason.HEAD_REQUEST_FAILED]:   "bg-pink-100 text-pink-700 border-pink-200",
  [WebScraperSkipReason.UNSUPPORTED_PROTOCOL]:  "bg-slate-100 text-slate-700 border-slate-200",
  [WebScraperSkipReason.UNKNOWN_CONTENT_TYPE]:  "bg-purple-100 text-purple-700 border-purple-200",
};

export default function SkippedUrlsPanel({ jobId, refreshKey }: SkippedUrlsPanelProps) {
  const [items, setItems] = useState<WebScraperSkippedUrl[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<Partial<Record<WebScraperSkipReason, number>>>({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [reason, setReason] = useState<WebScraperSkipReason | "">("");
  const [search, setSearch] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await webScraperService.listSkippedUrls(jobId, {
        ...(reason ? { reason } : {}),
        ...(search ? { search } : {}),
        skip: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      });
      setItems(result.list);
      setTotal(result.total);
      setSummary(result.summary || {});
    } catch {
      // non-fatal
    } finally {
      setLoading(false);
    }
  }, [jobId, page, reason, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const summaryEntries = Object.entries(summary) as [WebScraperSkipReason, number][];

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-200">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Skipped URLs</h2>
              <p className="text-xs text-gray-500">URLs the crawl saw but did not import, grouped by reason.</p>
            </div>
          </div>
          <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
            {total} total
          </span>
        </div>

        {/* Friendly explainer — most skipped URLs are normal */}
        <div className="mt-3 flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
          <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-blue-900 leading-relaxed">
            <strong>These URLs were intentionally not imported.</strong> Most are usually fine to ignore — external links,
            duplicate file types, pages beyond your depth limit, or files larger than your size cap. Use the chips below
            to filter by reason if something looks unexpected.
          </p>
        </div>

        {/* Summary chips — also act as filters */}
        {summaryEntries.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={() => { setReason(""); setPage(1); }}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                reason === ""
                  ? "bg-gray-800 text-white border-gray-800"
                  : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
              }`}
            >
              All
            </button>
            {summaryEntries.map(([r, count]) => (
              <button
                key={r}
                onClick={() => { setReason(r); setPage(1); }}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                  reason === r
                    ? REASON_COLORS[r]
                    : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                }`}
              >
                {REASON_LABELS[r]} <span className="opacity-70">·{count}</span>
              </button>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="mt-3 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by URL"
            className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="p-10 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
        </div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center">
          <AlertCircle className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No skipped URLs to show.</p>
          <p className="text-xs text-gray-400 mt-1">If the crawl is still running, more may appear soon.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-2.5">URL</th>
                <th className="px-4 py-2.5">Reason</th>
                <th className="px-4 py-2.5">Found on</th>
                <th className="px-4 py-2.5 text-center">Depth</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((it) => (
                <tr key={it._id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 max-w-md">
                    <div className="flex items-start gap-2">
                      <a
                        href={it.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-600 hover:text-purple-700 break-all text-xs font-medium flex items-center gap-1"
                        title={it.url}
                      >
                        {it.url.length > 90 ? `${it.url.slice(0, 90)}…` : it.url}
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      </a>
                    </div>
                    {it.detail && (
                      <p className="text-[11px] text-gray-500 mt-0.5">{it.detail}</p>
                    )}
                  </td>
                  <td className="px-4 py-2.5 align-top">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium border ${REASON_COLORS[it.reason]}`}>
                      {REASON_LABELS[it.reason] || it.reason}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 max-w-xs align-top">
                    {it.found_on_page_url ? (
                      <a
                        href={it.found_on_page_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-purple-600 break-all text-xs"
                        title={it.found_on_page_url}
                      >
                        {it.found_on_page_url.length > 60 ? `${it.found_on_page_url.slice(0, 60)}…` : it.found_on_page_url}
                      </a>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-center text-xs text-gray-600 align-top">
                    {it.depth}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between text-xs text-gray-600">
          <span>
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex items-center gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="p-1.5 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <span>Page {page} of {totalPages}</span>
            <button
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="p-1.5 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
